/**
 * Preference validation utilities for journey preferences.
 *
 * Validates preference values against defined allowlists and provides
 * smart defaults merging for returning travelers.
 */

/**
 * Schema defining valid types and allowed values for each preference key.
 */
export const PREFERENCE_SCHEMA = {
  transportMode: {
    type: 'enum',
    values: ['driving', 'rideshare', 'dropoff', 'public_transit'],
  },
  hasCheckedBags: {
    type: 'boolean',
  },
  wantsLoungeInfo: {
    type: 'boolean',
  },
  wantsGroundTransport: {
    type: 'boolean',
  },
  preferredGroundTransport: {
    type: 'enum',
    values: ['rideshare', 'rental_car', 'shuttle', 'public_transit'],
  },
  source: {
    type: 'enum',
    values: ['structured_ui', 'smart_default', 'quick_edit', 'trip_creation'],
  },
};

/**
 * Validate a single preference value against the schema.
 *
 * @param {string} key - Preference key
 * @param {any} value - Value to validate
 * @returns {{ valid: boolean, error?: string }}
 */
export function validatePreference(key, value) {
  if (!Object.prototype.hasOwnProperty.call(PREFERENCE_SCHEMA, key)) {
    return { valid: false, error: `Unknown preference key: ${key}` };
  }
  const schema = PREFERENCE_SCHEMA[key];

  if (schema.type === 'enum') {
    if (!schema.values.includes(value)) {
      return {
        valid: false,
        error: `Invalid value "${value}" for ${key}. Allowed: ${schema.values.join(', ')}`,
      };
    }
  }

  if (schema.type === 'boolean') {
    if (typeof value !== 'boolean') {
      return {
        valid: false,
        error: `${key} must be a boolean, got ${typeof value}`,
      };
    }
  }

  return { valid: true };
}

/**
 * Validate a complete preferences object.
 * Skips metadata fields (userId, tripId, updatedAt, legPreferences).
 *
 * @param {object} preferences - Preferences to validate
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validatePreferences(preferences) {
  const errors = [];
  for (const [key, value] of Object.entries(preferences)) {
    if (key === 'userId' || key === 'tripId' || key === 'updatedAt' || key === 'legPreferences') {
      continue;
    }
    const result = validatePreference(key, value);
    if (!result.valid) {
      errors.push(result.error);
    }
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Merge smart defaults with partially collected preferences.
 * Existing preferences take priority over defaults.
 *
 * @param {object|null} defaults - From getDefaultPreferences
 * @param {object|null} existing - Already collected preferences
 * @returns {object} Merged preferences
 */
export function mergeWithDefaults(defaults, existing) {
  if (!defaults && !existing) return {};
  if (!defaults) return { ...existing };
  if (!existing) return { ...defaults };

  const merged = { ...defaults };
  for (const [key, value] of Object.entries(existing)) {
    if (value !== undefined && value !== null) {
      merged[key] = value;
    }
  }
  return merged;
}
